import { useState } from 'react';

import { NavLink as RouterLink } from 'react-router-dom';

import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import {
  Button,
  Center,
  Flex,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  Select,
} from '@chakra-ui/react';
import {
  ActionIconButton,
  AddCircleOutline,
  CheckCircleOutline,
  ClockOutline,
  CompanyPartnerIntegration,
  DeleteOutline,
  EditOutline,
  IntegrationStatus,
  IntegrationSyncStatus,
  IntegrationType,
  useToast,
  WarningOutline,
} from '@aslanpay/portal-common-lib';

import useFetchCompanyIntegrations from 'app/hooks/useFetchCompanyIntegrations';
import useUpdateIntegrationStatus from 'app/hooks/useUpdateIntegrationStatus';
import useDeleteIntegration from 'app/hooks/useDeleteIntegration';
import useAddIntegrationType from 'app/hooks/useAddIntegrationType';
import useUpdateIntegrationTypeStatus from 'app/hooks/useUpdateIntegrationTypeStatus';
import useRemoveIntegrationType from 'app/hooks/useRemoveIntegrationType';
import useTriggerWorkdaySync from 'app/hooks/useTriggerWorkdaySync';

import { SYNC_STATUS_DETAILS } from 'app/utils/integrationSync';
import { getIntegrationSyncHistoryPath } from 'app/utils/routes';

import { AddTypeMenu } from '../AddTypeMenu';
import { RegisterIntegrationModal } from '../RegisterIntegrationModal';
import { EditIntegrationModal } from '../EditIntegrationModal';

import { PartnerIntegrationsSectionProps } from './types';

const PROVIDER_LABELS: Record<string, string> = {
  WORKDAY: 'Workday',
  CEZANNE: 'Cezanne',
};

const ALL_INTEGRATION_TYPES: IntegrationType[] = ['HRIS_SYNC', 'TIMESHEET_SYNC', 'PAYROLL_OUTPUT'];

const INTEGRATION_TYPE_LABELS: Record<string, string> = {
  HRIS_SYNC: 'HRIS Sync',
  TIMESHEET_SYNC: 'Timesheet Sync',
  PAYROLL_OUTPUT: 'Payroll Output',
};

const AUTH_TYPE_LABELS: Record<string, string> = {
  NONE: 'None',
  API_KEY_HEADER: 'API key header',
  BEARER_TOKEN: 'Bearer token',
  OAUTH_CLIENT_CREDENTIALS: 'OAuth client credentials',
  OAUTH_REFRESH_TOKEN: 'OAuth refresh token',
  BASIC: 'Basic',
  WS_SECURITY_USERNAME_TOKEN: 'WS-Security username token',
};

const STATUS_OPTIONS: IntegrationStatus[] = ['ACTIVE', 'PAUSED', 'DISABLED'];

const MANUAL_SYNC_TYPES: IntegrationType[] = ['HRIS_SYNC', 'TIMESHEET_SYNC'];

const isManualSyncType = (
  integrationType: IntegrationType
): integrationType is Extract<IntegrationType, 'HRIS_SYNC' | 'TIMESHEET_SYNC'> =>
  MANUAL_SYNC_TYPES.includes(integrationType);

const SYNC_STATUS_ICON_COLORS: Record<IntegrationSyncStatus, string> = {
  SUCCESS: 'green.500',
  PARTIAL_SUCCESS: 'orange.500',
  FAILED: 'red.500',
};

const isActiveIntegrationType = (
  integration: CompanyPartnerIntegration,
  status: IntegrationStatus
) => integration.status === 'ACTIVE' && status === 'ACTIVE';

const formatRelativeSyncDate = (lastSyncedAt: string) =>
  formatDistanceToNowStrict(parseISO(lastSyncedAt), { addSuffix: true });

const getSyncDetails = (
  lastSyncedAt: string,
  lastSyncStatus: IntegrationSyncStatus,
  lastSyncError?: string,
  lastSyncProcessedCount?: number,
  lastSyncSucceededCount?: number
) => {
  const details = [
    `Last synced: ${format(parseISO(lastSyncedAt), 'dd MMM yyyy, HH:mm')}`,
    `Last sync: ${SYNC_STATUS_DETAILS[lastSyncStatus].label.toLowerCase()}`,
  ];

  if (lastSyncProcessedCount !== undefined && lastSyncSucceededCount !== undefined) {
    details.push(`Processed: ${lastSyncSucceededCount} / ${lastSyncProcessedCount}`);
  }

  if (lastSyncError) {
    details.push(`Error: ${lastSyncError}`);
  }

  return details.join('\n');
};

function PartnerIntegrationsSection({ companyId = '' }: PartnerIntegrationsSectionProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [editingIntegration, setEditingIntegration] = useState<CompanyPartnerIntegration | null>(
    null
  );
  const [syncingIntegrationType, setSyncingIntegrationType] = useState<IntegrationType | null>(
    null
  );
  const toast = useToast();

  const { data: integrations, isLoading, isError } = useFetchCompanyIntegrations(companyId);
  const updateStatusMutation = useUpdateIntegrationStatus();
  const deleteMutation = useDeleteIntegration();
  const addTypeMutation = useAddIntegrationType();
  const updateTypeStatusMutation = useUpdateIntegrationTypeStatus();
  const removeTypeMutation = useRemoveIntegrationType();
  const triggerWorkdaySyncMutation = useTriggerWorkdaySync();

  const hasIntegrations = !!integrations && integrations.length > 0;

  const showSuccess = (title: string, description: string) =>
    toast({ position: 'top-right', status: 'success', title, description });

  const showError = (description: string) =>
    toast({
      position: 'top-right',
      status: 'error',
      title: 'Something went wrong',
      description,
    });

  const handleStatusChange = async (
    integration: CompanyPartnerIntegration,
    status: IntegrationStatus
  ) => {
    setUpdatingId(integration.id);
    try {
      await updateStatusMutation.mutateAsync({
        id: integration.id,
        companyId,
        body: { status },
      });
      showSuccess('Status updated', 'The connection status has been successfully updated.');
    } catch {
      showError('Sorry, the status could not be updated. Please try again.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (integration: CompanyPartnerIntegration) => {
    try {
      await deleteMutation.mutateAsync({ id: integration.id, companyId });
      showSuccess('Integration deleted', 'The partner integration has been successfully deleted.');
    } catch {
      showError('Sorry, the integration could not be deleted. Please try again.');
    }
  };

  const handleAddType = async (
    integration: CompanyPartnerIntegration,
    integrationType: IntegrationType,
    config?: Record<string, string>
  ) => {
    try {
      await addTypeMutation.mutateAsync({ id: integration.id, companyId, integrationType, config });
      showSuccess('Type added', 'The integration type has been successfully added.');
    } catch (error) {
      showError('Sorry, the integration type could not be added. Please try again.');
      // Rethrown so AddTypeMenu's modal (when one is open) knows to stay open for a retry.
      throw error;
    }
  };

  const handleTypeStatusChange = async (
    integration: CompanyPartnerIntegration,
    integrationType: IntegrationType,
    status: IntegrationStatus
  ) => {
    try {
      await updateTypeStatusMutation.mutateAsync({
        id: integration.id,
        companyId,
        integrationType,
        status,
      });
      showSuccess('Type status updated', 'The integration type status has been updated.');
    } catch {
      showError('Sorry, the type status could not be updated. Please try again.');
    }
  };

  const handleRemoveType = async (
    integration: CompanyPartnerIntegration,
    integrationType: IntegrationType
  ) => {
    try {
      await removeTypeMutation.mutateAsync({ id: integration.id, companyId, integrationType });
      showSuccess('Type removed', 'The integration type has been successfully removed.');
    } catch {
      showError('Sorry, the integration type could not be removed. Please try again.');
    }
  };

  const handleManualSync = async (
    integrationType: Extract<IntegrationType, 'HRIS_SYNC' | 'TIMESHEET_SYNC'>
  ) => {
    setSyncingIntegrationType(integrationType);
    try {
      await triggerWorkdaySyncMutation.mutateAsync({
        companyId,
        integrationType,
      });
      showSuccess(
        'Sync started',
        `The ${INTEGRATION_TYPE_LABELS[integrationType]} has been started.`
      );
    } catch {
      showError(`Sorry, the ${INTEGRATION_TYPE_LABELS[integrationType]} could not be started.`);
    } finally {
      setSyncingIntegrationType(null);
    }
  };

  const availableTypes = (integration: CompanyPartnerIntegration) =>
    ALL_INTEGRATION_TYPES.filter(
      (type) => !integration.types.some((enabled) => enabled.integrationType === type)
    );

  return (
    <Flex flexDir="column" gap="6px">
      <Flex justify="space-between" align="center">
        <Text as="h6" textStyle="h6SemiBold">
          Partner integrations
        </Text>
        {hasIntegrations && (
          <Button
            leftIcon={<AddCircleOutline boxSize="18px" />}
            size="sm"
            variant="outline"
            onClick={onOpen}
          >
            Add integration
          </Button>
        )}
      </Flex>
      <Text textStyle="extraSmallRegular" color="brand.darkGray">
        Configure which partner (e.g. Workday) syncs data for this company. A connection holds one
        set of credentials per provider and protocol, and can be enabled for multiple integration
        types.
      </Text>

      {isLoading && (
        <Center py="40px">
          <Spinner color="brand.purple" size="md" />
        </Center>
      )}

      {isError && (
        <Text textStyle="extraSmallRegular" color="red.500" mt="8px">
          Failed to load partner integrations.
        </Text>
      )}

      {!isLoading && !isError && hasIntegrations && (
        <Flex
          mt="14px"
          overflowX="auto"
          border="1px solid"
          borderColor="brand.lightGray1"
          borderRadius="8px"
        >
          <Table variant="simple" size="sm">
            <Thead bgColor="brand.lightGray2">
              <Tr>
                <Th>Provider</Th>
                <Th>Protocol</Th>
                <Th>Authentication</Th>
                <Th>Integration types</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {integrations.map((integration: CompanyPartnerIntegration) => (
                <Tr key={integration.id}>
                  <Td fontSize="sm">
                    {PROVIDER_LABELS[integration.provider] ?? integration.provider}
                  </Td>
                  <Td fontSize="sm">
                    <Text>{integration.protocol}</Text>
                    {!!integration.baseUrl && (
                      <Text textStyle="extraSmallRegular" color="brand.darkGray">
                        {integration.baseUrl}
                      </Text>
                    )}
                  </Td>
                  <Td fontSize="sm">
                    <Text>
                      {AUTH_TYPE_LABELS[integration.authType ?? 'NONE'] ?? integration.authType}
                    </Text>
                    {!!integration.configuredSecretFields?.length && (
                      <Text textStyle="extraSmallRegular" color="brand.darkGray">
                        Secrets set: {integration.configuredSecretFields.join(', ')}
                      </Text>
                    )}
                  </Td>
                  <Td fontSize="sm">
                    <Flex flexDir="column" gap="4px">
                      {integration.types.map((type) => (
                        <Flex key={type.integrationType} align="center" gap="6px">
                          <Flex flexDir="column" minW="180px">
                            <Text>
                              {INTEGRATION_TYPE_LABELS[type.integrationType] ??
                                type.integrationType}
                            </Text>
                            {isActiveIntegrationType(integration, type.status) &&
                              type.lastSyncedAt && (
                                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                                  Last synced: {formatRelativeSyncDate(type.lastSyncedAt)}
                                </Text>
                              )}
                            {isActiveIntegrationType(integration, type.status) &&
                              type.lastSyncedAt &&
                              type.lastSyncStatus && (
                                <Tooltip
                                  hasArrow
                                  label={getSyncDetails(
                                    type.lastSyncedAt,
                                    type.lastSyncStatus,
                                    type.lastSyncError,
                                    type.lastSyncProcessedCount,
                                    type.lastSyncSucceededCount
                                  )}
                                  placement="top"
                                  whiteSpace="pre-line"
                                >
                                  <Flex
                                    align="center"
                                    aria-label={`Last sync ${SYNC_STATUS_DETAILS[
                                      type.lastSyncStatus
                                    ].label.toLowerCase()}`}
                                    color={SYNC_STATUS_ICON_COLORS[type.lastSyncStatus]}
                                    role="img"
                                    w="fit-content"
                                  >
                                    {type.lastSyncStatus === 'SUCCESS' ? (
                                      <CheckCircleOutline boxSize="16px" />
                                    ) : (
                                      <WarningOutline boxSize="16px" />
                                    )}
                                  </Flex>
                                </Tooltip>
                              )}
                          </Flex>
                          {isActiveIntegrationType(integration, type.status) &&
                            type.lastSyncedAt && (
                              <Tooltip label="View sync history" placement="top" shouldWrapChildren>
                                <Link
                                  as={RouterLink}
                                  to={getIntegrationSyncHistoryPath(
                                    companyId,
                                    type.integrationType
                                  )}
                                  color="brand.darkGray"
                                  aria-label={`View sync history for ${type.integrationType}`}
                                >
                                  <ClockOutline boxSize="14px" />
                                </Link>
                              </Tooltip>
                            )}
                          <Select
                            size="xs"
                            w="110px"
                            value={type.status}
                            onChange={(e) =>
                              handleTypeStatusChange(
                                integration,
                                type.integrationType,
                                e.target.value as IntegrationStatus
                              )
                            }
                            aria-label={`Status for type ${type.integrationType}`}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {s.charAt(0) + s.slice(1).toLowerCase()}
                              </option>
                            ))}
                          </Select>
                          <ActionIconButton
                            size="xs"
                            colorScheme="red"
                            icon={<DeleteOutline boxSize="14px" />}
                            aria-label={`Remove type ${type.integrationType}`}
                            onClick={() => handleRemoveType(integration, type.integrationType)}
                          />
                          {isActiveIntegrationType(integration, type.status) &&
                            isManualSyncType(type.integrationType) && (
                              <Button
                                size="xs"
                                variant="outline"
                                isDisabled={syncingIntegrationType !== null}
                                isLoading={syncingIntegrationType === type.integrationType}
                                data-testid={`workday-sync-${type.integrationType
                                  .toLowerCase()
                                  .replace('_', '-')}`}
                                onClick={() =>
                                  handleManualSync(
                                    type.integrationType as Extract<
                                      IntegrationType,
                                      'HRIS_SYNC' | 'TIMESHEET_SYNC'
                                    >
                                  )
                                }
                              >
                                Sync now
                              </Button>
                            )}
                        </Flex>
                      ))}
                      {availableTypes(integration).length > 0 && (
                        <AddTypeMenu
                          integration={integration}
                          availableTypes={availableTypes(integration)}
                          typeLabels={INTEGRATION_TYPE_LABELS}
                          onAdd={handleAddType}
                        />
                      )}
                    </Flex>
                  </Td>
                  <Td fontSize="sm">
                    <Select
                      size="sm"
                      value={integration.status}
                      isDisabled={updatingId === integration.id}
                      onChange={(e) =>
                        handleStatusChange(integration, e.target.value as IntegrationStatus)
                      }
                      aria-label={`Status for ${integration.provider} ${integration.protocol} connection`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s.charAt(0) + s.slice(1).toLowerCase()}
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td>
                    <Flex gap="2px">
                      <ActionIconButton
                        icon={<EditOutline boxSize="16px" />}
                        aria-label="Edit integration"
                        onClick={() => setEditingIntegration(integration)}
                      />
                      <ActionIconButton
                        icon={<DeleteOutline boxSize="16px" />}
                        aria-label="Delete integration"
                        colorScheme="red"
                        isLoading={
                          deleteMutation.isPending &&
                          deleteMutation.variables?.id === integration.id
                        }
                        onClick={() => handleDelete(integration)}
                      />
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Flex>
      )}

      {!isLoading && !isError && !hasIntegrations && (
        <Flex
          mt="14px"
          w="100%"
          py="40px"
          px="20px"
          bgColor="brand.lightGray2"
          borderRadius="8px"
          justify="center"
          align="center"
          flexDir="column"
          gap="16px"
        >
          <Text textStyle="body" color="brand.darkGray">
            No partner integrations configured yet
          </Text>
          {companyId && (
            <Button leftIcon={<AddCircleOutline boxSize="20px" />} size="sm" onClick={onOpen}>
              Add your first integration
            </Button>
          )}
        </Flex>
      )}

      {companyId && isOpen && (
        <RegisterIntegrationModal companyId={companyId} isOpen={isOpen} onClose={onClose} />
      )}

      {companyId && editingIntegration && (
        <EditIntegrationModal
          companyId={companyId}
          integration={editingIntegration}
          isOpen
          onClose={() => setEditingIntegration(null)}
        />
      )}
    </Flex>
  );
}

export { PartnerIntegrationsSection };

export default PartnerIntegrationsSection;
